import type { Conference, ConferenceId, Team } from './types.js';

/**
 * Complete FBS team database.
 *
 * Rows are pipe-delimited to keep the source readable and diff-friendly:
 *   school | nickname | abbreviation | conferenceId | division | primary | secondary | city | state
 *
 * Conference alignment reflects the 2026 season. When a live
 * TeamDataProvider is connected, these rows become the fallback/seed set and
 * provider data wins (see api/src/providers/).
 */

export const CONFERENCES: Conference[] = [
  { id: 'sec', name: 'Southeastern Conference', shortName: 'SEC', tier: 'power' },
  { id: 'big-ten', name: 'Big Ten Conference', shortName: 'Big Ten', tier: 'power' },
  { id: 'big-12', name: 'Big 12 Conference', shortName: 'Big 12', tier: 'power' },
  { id: 'acc', name: 'Atlantic Coast Conference', shortName: 'ACC', tier: 'power' },
  { id: 'pac-12', name: 'Pac-12 Conference', shortName: 'Pac-12', tier: 'group' },
  { id: 'american', name: 'American Conference', shortName: 'American', tier: 'group' },
  { id: 'mountain-west', name: 'Mountain West Conference', shortName: 'Mountain West', tier: 'group' },
  { id: 'mac', name: 'Mid-American Conference', shortName: 'MAC', tier: 'group' },
  {
    id: 'sun-belt',
    name: 'Sun Belt Conference',
    shortName: 'Sun Belt',
    tier: 'group',
    divisions: ['East', 'West'],
  },
  { id: 'conference-usa', name: 'Conference USA', shortName: 'CUSA', tier: 'group' },
  { id: 'independent', name: 'FBS Independents', shortName: 'Independent', tier: 'independent' },
];

const TEAM_ROWS = [
  // --------------------------- SEC (16) ---------------------------
  'Alabama|Crimson Tide|ALA|sec||#9E1B32|#828A8F|Tuscaloosa|AL',
  'Arkansas|Razorbacks|ARK|sec||#9D2235|#FFFFFF|Fayetteville|AR',
  'Auburn|Tigers|AUB|sec||#0C2340|#E87722|Auburn|AL',
  'Florida|Gators|FLA|sec||#0021A5|#FA4616|Gainesville|FL',
  'Georgia|Bulldogs|UGA|sec||#BA0C2F|#000000|Athens|GA',
  'Kentucky|Wildcats|UK|sec||#0033A0|#FFFFFF|Lexington|KY',
  'LSU|Tigers|LSU|sec||#461D7C|#FDD023|Baton Rouge|LA',
  'Mississippi State|Bulldogs|MSST|sec||#5D1725|#FFFFFF|Starkville|MS',
  'Missouri|Tigers|MIZ|sec||#F1B82D|#000000|Columbia|MO',
  'Oklahoma|Sooners|OU|sec||#841617|#FDF9F7|Norman|OK',
  'Ole Miss|Rebels|MISS|sec||#14213D|#CE1126|Oxford|MS',
  'South Carolina|Gamecocks|SC|sec||#73000A|#000000|Columbia|SC',
  'Tennessee|Volunteers|TENN|sec||#FF8200|#58595B|Knoxville|TN',
  'Texas|Longhorns|TEX|sec||#BF5700|#FFFFFF|Austin|TX',
  'Texas A&M|Aggies|TA&M|sec||#500000|#FFFFFF|College Station|TX',
  'Vanderbilt|Commodores|VAN|sec||#866D4B|#000000|Nashville|TN',

  // ------------------------- Big Ten (18) -------------------------
  'Illinois|Fighting Illini|ILL|big-ten||#13294B|#E84A27|Champaign|IL',
  'Indiana|Hoosiers|IU|big-ten||#990000|#EEEDEB|Bloomington|IN',
  'Iowa|Hawkeyes|IOWA|big-ten||#000000|#FFCD00|Iowa City|IA',
  'Maryland|Terrapins|MD|big-ten||#E03A3E|#FFD520|College Park|MD',
  'Michigan|Wolverines|MICH|big-ten||#00274C|#FFCB05|Ann Arbor|MI',
  'Michigan State|Spartans|MSU|big-ten||#18453B|#FFFFFF|East Lansing|MI',
  'Minnesota|Golden Gophers|MINN|big-ten||#7A0019|#FFCC33|Minneapolis|MN',
  'Nebraska|Cornhuskers|NEB|big-ten||#E41C38|#F5F1E7|Lincoln|NE',
  'Northwestern|Wildcats|NU|big-ten||#4E2A84|#FFFFFF|Evanston|IL',
  'Ohio State|Buckeyes|OSU|big-ten||#BB0000|#666666|Columbus|OH',
  'Oregon|Ducks|ORE|big-ten||#154733|#FEE123|Eugene|OR',
  'Penn State|Nittany Lions|PSU|big-ten||#041E42|#FFFFFF|University Park|PA',
  'Purdue|Boilermakers|PUR|big-ten||#CEB888|#000000|West Lafayette|IN',
  'Rutgers|Scarlet Knights|RUTG|big-ten||#CC0033|#5F6A72|Piscataway|NJ',
  'UCLA|Bruins|UCLA|big-ten||#2D68C4|#F2A900|Los Angeles|CA',
  'USC|Trojans|USC|big-ten||#990000|#FFC72C|Los Angeles|CA',
  'Washington|Huskies|WASH|big-ten||#4B2E83|#B7A57A|Seattle|WA',
  'Wisconsin|Badgers|WIS|big-ten||#C5050C|#FFFFFF|Madison|WI',

  // -------------------------- Big 12 (16) --------------------------
  'Arizona|Wildcats|ARIZ|big-12||#AB0520|#0C234B|Tucson|AZ',
  'Arizona State|Sun Devils|ASU|big-12||#8C1D40|#FFC627|Tempe|AZ',
  'Baylor|Bears|BAY|big-12||#154734|#FFB81C|Waco|TX',
  'BYU|Cougars|BYU|big-12||#002E5D|#FFFFFF|Provo|UT',
  'Cincinnati|Bearcats|CIN|big-12||#E00122|#000000|Cincinnati|OH',
  'Colorado|Buffaloes|COLO|big-12||#CFB87C|#000000|Boulder|CO',
  'Houston|Cougars|HOU|big-12||#C8102E|#FFFFFF|Houston|TX',
  'Iowa State|Cyclones|ISU|big-12||#C8102E|#F1BE48|Ames|IA',
  'Kansas|Jayhawks|KU|big-12||#0051BA|#E8000D|Lawrence|KS',
  'Kansas State|Wildcats|KSU|big-12||#512888|#D1D1D1|Manhattan|KS',
  'Oklahoma State|Cowboys|OKST|big-12||#FF7300|#000000|Stillwater|OK',
  'TCU|Horned Frogs|TCU|big-12||#4D1979|#A3A9AC|Fort Worth|TX',
  'Texas Tech|Red Raiders|TTU|big-12||#CC0000|#000000|Lubbock|TX',
  'UCF|Knights|UCF|big-12||#000000|#BA9B37|Orlando|FL',
  'Utah|Utes|UTAH|big-12||#CC0000|#FFFFFF|Salt Lake City|UT',
  'West Virginia|Mountaineers|WVU|big-12||#002855|#EAAA00|Morgantown|WV',

  // --------------------------- ACC (17) ---------------------------
  'Boston College|Eagles|BC|acc||#98002E|#BC9B6A|Chestnut Hill|MA',
  'California|Golden Bears|CAL|acc||#003262|#FDB515|Berkeley|CA',
  'Clemson|Tigers|CLEM|acc||#F56600|#522D80|Clemson|SC',
  'Duke|Blue Devils|DUKE|acc||#003087|#FFFFFF|Durham|NC',
  'Florida State|Seminoles|FSU|acc||#782F40|#CEB888|Tallahassee|FL',
  'Georgia Tech|Yellow Jackets|GT|acc||#B3A369|#003057|Atlanta|GA',
  'Louisville|Cardinals|LOU|acc||#AD0000|#000000|Louisville|KY',
  'Miami|Hurricanes|MIA|acc||#F47321|#005030|Coral Gables|FL',
  'NC State|Wolfpack|NCSU|acc||#CC0000|#000000|Raleigh|NC',
  'North Carolina|Tar Heels|UNC|acc||#7BAFD4|#13294B|Chapel Hill|NC',
  'Pittsburgh|Panthers|PITT|acc||#003594|#FFB81C|Pittsburgh|PA',
  'SMU|Mustangs|SMU|acc||#0033A0|#C8102E|Dallas|TX',
  'Stanford|Cardinal|STAN|acc||#8C1515|#FFFFFF|Stanford|CA',
  'Syracuse|Orange|SYR|acc||#F76900|#000E54|Syracuse|NY',
  'Virginia|Cavaliers|UVA|acc||#232D4B|#F84C1E|Charlottesville|VA',
  'Virginia Tech|Hokies|VT|acc||#630031|#CF4420|Blacksburg|VA',
  'Wake Forest|Demon Deacons|WAKE|acc||#9E7E38|#000000|Winston-Salem|NC',

  // -------------------------- Pac-12 (8) --------------------------
  'Boise State|Broncos|BOIS|pac-12||#0033A0|#D64309|Boise|ID',
  'Colorado State|Rams|CSU|pac-12||#1E4D2B|#C8C372|Fort Collins|CO',
  'Fresno State|Bulldogs|FRES|pac-12||#DB0032|#002E6D|Fresno|CA',
  'Oregon State|Beavers|ORST|pac-12||#DC4405|#000000|Corvallis|OR',
  'San Diego State|Aztecs|SDSU|pac-12||#A6192E|#000000|San Diego|CA',
  'Texas State|Bobcats|TXST|pac-12||#501214|#AF9800|San Marcos|TX',
  'Utah State|Aggies|USU|pac-12||#00263A|#8A8D8F|Logan|UT',
  'Washington State|Cougars|WSU|pac-12||#981E32|#5E6A71|Pullman|WA',

  // ------------------------- American (14) -------------------------
  'Army|Black Knights|ARMY|american||#2C2A29|#D4BF91|West Point|NY',
  'Charlotte|49ers|CLT|american||#046A38|#B9975B|Charlotte|NC',
  'East Carolina|Pirates|ECU|american||#592A8A|#FDC82F|Greenville|NC',
  'Florida Atlantic|Owls|FAU|american||#003366|#CC0000|Boca Raton|FL',
  'Memphis|Tigers|MEM|american||#003087|#898D8D|Memphis|TN',
  'Navy|Midshipmen|NAVY|american||#00205B|#C5B783|Annapolis|MD',
  'North Texas|Mean Green|UNT|american||#00853E|#000000|Denton|TX',
  'Rice|Owls|RICE|american||#00205B|#C1C6C8|Houston|TX',
  'South Florida|Bulls|USF|american||#006747|#CFC493|Tampa|FL',
  'Temple|Owls|TEM|american||#9D2235|#FFFFFF|Philadelphia|PA',
  'Tulane|Green Wave|TULN|american||#006747|#418FDE|New Orleans|LA',
  'Tulsa|Golden Hurricane|TLSA|american||#002D72|#C8102E|Tulsa|OK',
  'UAB|Blazers|UAB|american||#1E6B52|#D4A017|Birmingham|AL',
  'UTSA|Roadrunners|UTSA|american||#0C2340|#F15A22|San Antonio|TX',

  // ---------------------- Mountain West (10) ----------------------
  'Air Force|Falcons|AFA|mountain-west||#003087|#8A8D8F|Colorado Springs|CO',
  'Hawaii|Rainbow Warriors|HAW|mountain-west||#024731|#C8C8C8|Honolulu|HI',
  'Nevada|Wolf Pack|NEV|mountain-west||#003366|#807F84|Reno|NV',
  'New Mexico|Lobos|UNM|mountain-west||#BA0C2F|#63666A|Albuquerque|NM',
  'North Dakota State|Bison|NDSU|mountain-west||#009639|#FFC72C|Fargo|ND',
  'Northern Illinois|Huskies|NIU|mountain-west||#BA0C2F|#000000|DeKalb|IL',
  'San Jose State|Spartans|SJSU|mountain-west||#0055A2|#E5A823|San Jose|CA',
  'UNLV|Rebels|UNLV|mountain-west||#CF0A2C|#666666|Las Vegas|NV',
  'UTEP|Miners|UTEP|mountain-west||#041E42|#FF8200|El Paso|TX',
  'Wyoming|Cowboys|WYO|mountain-west||#492F24|#FFC425|Laramie|WY',

  // --------------------------- MAC (13) ---------------------------
  'Akron|Zips|AKR|mac||#041E42|#A89968|Akron|OH',
  'Ball State|Cardinals|BALL|mac||#BA0C2F|#000000|Muncie|IN',
  'Bowling Green|Falcons|BGSU|mac||#FE5000|#4F2C1D|Bowling Green|OH',
  'Buffalo|Bulls|BUFF|mac||#005BBB|#000000|Buffalo|NY',
  'Central Michigan|Chippewas|CMU|mac||#6A0032|#FFC82E|Mount Pleasant|MI',
  'Eastern Michigan|Eagles|EMU|mac||#046A38|#000000|Ypsilanti|MI',
  'Kent State|Golden Flashes|KENT|mac||#002664|#EAAB00|Kent|OH',
  'Massachusetts|Minutemen|MASS|mac||#881C1C|#000000|Amherst|MA',
  'Miami (OH)|RedHawks|M-OH|mac||#C3142D|#000000|Oxford|OH',
  'Ohio|Bobcats|OHIO|mac||#00694E|#CDA077|Athens|OH',
  'Sacramento State|Hornets|SAC|mac||#00563F|#C4B581|Sacramento|CA',
  'Toledo|Rockets|TOL|mac||#15397F|#FFC82E|Toledo|OH',
  'Western Michigan|Broncos|WMU|mac||#6C4023|#B5A167|Kalamazoo|MI',

  // ------------------------- Sun Belt (14) -------------------------
  'Appalachian State|Mountaineers|APP|sun-belt|East|#000000|#FFCC00|Boone|NC',
  'Arkansas State|Red Wolves|ARST|sun-belt|West|#CC092F|#000000|Jonesboro|AR',
  'Coastal Carolina|Chanticleers|CCU|sun-belt|East|#006F71|#A27752|Conway|SC',
  'Georgia Southern|Eagles|GASO|sun-belt|East|#011E41|#87714D|Statesboro|GA',
  'Georgia State|Panthers|GAST|sun-belt|East|#0039A6|#C60C30|Atlanta|GA',
  'James Madison|Dukes|JMU|sun-belt|East|#450084|#CBB677|Harrisonburg|VA',
  'Louisiana|Ragin Cajuns|UL|sun-belt|West|#CE181E|#000000|Lafayette|LA',
  'Louisiana Tech|Bulldogs|LT|sun-belt||#002F8B|#E31B23|Ruston|LA',
  'Louisiana-Monroe|Warhawks|ULM|sun-belt|West|#840029|#FDB927|Monroe|LA',
  'Marshall|Thundering Herd|MRSH|sun-belt|East|#00B140|#000000|Huntington|WV',
  'Old Dominion|Monarchs|ODU|sun-belt|East|#003057|#A1A1A4|Norfolk|VA',
  'South Alabama|Jaguars|USA|sun-belt|West|#00205B|#BF0D3E|Mobile|AL',
  'Southern Miss|Golden Eagles|USM|sun-belt|West|#000000|#FFAA3C|Hattiesburg|MS',
  'Troy|Trojans|TROY|sun-belt|West|#8A2432|#C0C0C0|Troy|AL',

  // ---------------------- Conference USA (10) ----------------------
  'Delaware|Blue Hens|DEL|conference-usa||#00539F|#FFD200|Newark|DE',
  'Florida International|Panthers|FIU|conference-usa||#081E3F|#B6862C|Miami|FL',
  'Jacksonville State|Gamecocks|JXST|conference-usa||#CC0000|#000000|Jacksonville|AL',
  'Kennesaw State|Owls|KENN|conference-usa||#231F20|#FDBB30|Kennesaw|GA',
  'Liberty|Flames|LIB|conference-usa||#002D62|#A6192E|Lynchburg|VA',
  'Middle Tennessee|Blue Raiders|MTSU|conference-usa||#0066CC|#000000|Murfreesboro|TN',
  'Missouri State|Bears|MOST|conference-usa||#5E0009|#FFFFFF|Springfield|MO',
  'New Mexico State|Aggies|NMSU|conference-usa||#8C0B42|#000000|Las Cruces|NM',
  'Sam Houston|Bearkats|SHSU|conference-usa||#F56600|#FFFFFF|Huntsville|TX',
  'Western Kentucky|Hilltoppers|WKU|conference-usa||#C60C30|#000000|Bowling Green|KY',

  // ----------------------- Independents (2) -----------------------
  'Notre Dame|Fighting Irish|ND|independent||#0C2340|#C99700|Notre Dame|IN',
  'UConn|Huskies|CONN|independent||#000E2F|#E4002B|Storrs|CT',
] as const;

/** Stable slug used as the Cosmos id and the logo filename. */
export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/&/g, 'and')
    .replace(/[().']/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function parseRow(row: string): Team {
  const [school, nickname, abbreviation, conferenceId, division, primaryColor, secondaryColor, city, state] =
    row.split('|');
  return {
    id: slugify(school),
    school,
    nickname,
    abbreviation,
    conferenceId: conferenceId as ConferenceId,
    division: division || undefined,
    primaryColor,
    secondaryColor,
    city,
    state,
  };
}

export const FBS_TEAMS: Team[] = TEAM_ROWS.map(parseRow);

export const TEAMS_BY_ID: Record<string, Team> = Object.fromEntries(
  FBS_TEAMS.map((team) => [team.id, team]),
);

export const CONFERENCES_BY_ID: Record<string, Conference> = Object.fromEntries(
  CONFERENCES.map((conference) => [conference.id, conference]),
);

export function conferenceName(conferenceId: ConferenceId): string {
  return CONFERENCES_BY_ID[conferenceId]?.shortName ?? 'FBS';
}

export function teamsByConference(conferenceId: ConferenceId): Team[] {
  return FBS_TEAMS.filter((team) => team.conferenceId === conferenceId);
}
